import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

type FormState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'success', message: string }
    | { status: 'error', message: string }

interface SubscribeResponse {
    status?: 'subscribed' | 'already'
    message?: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const NewsletterForm: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [state, setState] = useState<FormState>({ status: 'idle' })
    const honeypotRef = useRef<HTMLInputElement>(null)

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value)

        if (state.status === 'error' || state.status === 'success') setState({ status: 'idle' })
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const value = email.trim()

        if (!EMAIL_PATTERN.test(value) || value.length > 254) {
            setState({ status: 'error', message: 'Please enter a valid email address.' })
            return
        }

        setState({ status: 'loading' })

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: value, company: honeypotRef.current?.value ?? '' })
            })

            const data = await response.json().catch(() => null) as SubscribeResponse | null

            if (!response.ok) {
                setState({ status: 'error', message: data?.message ?? 'Something went wrong. Please try again.' })
                return
            }

            setEmail('')
            setState({
                status: 'success',
                message: data?.status === 'already'
                    ? "You're already on the list. Thanks for sticking around."
                    : "Thanks for subscribing. The next issue will land in your inbox."
            })
        }
        catch {
            setState({ status: 'error', message: "We couldn't reach the server. Please try again." })
        }
    }

    const isError = state.status === 'error'

    return (
        <div className='w-full md:w-[430px]'>
            <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3 sm:flex-row'>
                <label htmlFor='newsletter-email' className='sr-only'>
                    Email address
                </label>

                <input
                    id='newsletter-email'
                    type='email'
                    name='email'
                    autoComplete='email'
                    inputMode='email'
                    required
                    value={email}
                    onChange={handleChange}
                    placeholder='you@company.com'
                    aria-invalid={isError}
                    aria-describedby='newsletter-status'
                    className={cn(
                        'h-[42px] min-w-0 flex-1 rounded-lg border bg-[#0d0e10] px-3.5 text-[15px] text-white outline-none transition-colors placeholder:text-[#5d5e62] focus:border-white/30 focus-visible:ring-2 focus-visible:ring-white/15',
                        isError ? 'border-[#ff7b7b]/60' : 'border-white/10'
                    )}
                />

                <div aria-hidden='true' className='absolute -left-[9999px] size-px overflow-hidden'>
                    <label htmlFor='newsletter-company'>
                        Company
                    </label>

                    <input
                        id='newsletter-company'
                        ref={honeypotRef}
                        type='text'
                        name='company'
                        tabIndex={-1}
                        autoComplete='off'
                    />
                </div>

                <button
                    type='submit'
                    disabled={state.status === 'loading'}
                    className='inline-flex h-[42px] shrink-0 items-center justify-center gap-2 rounded-lg bg-[#d9d9dc] px-8 text-[15px] font-medium text-[#0b0b0d] transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-wait disabled:opacity-70'
                >
                    {state.status === 'loading' && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
                    Subscribe
                </button>
            </form>

            <p
                id='newsletter-status'
                role='status'
                aria-live='polite'
                className={cn(
                    'flex items-center gap-1.5 text-[13px]',
                    (state.status === 'success' || isError) && 'mt-3',
                    isError ? 'text-[#ff8a8a]' : 'text-[#d7f5df]'
                )}
            >
                {state.status === 'success' && <Check className='size-3.5 shrink-0' aria-hidden='true' />}
                {state.status === 'success' || isError ? state.message : null}
            </p>

            <p className='mt-4 max-w-[400px] text-[12.5px] leading-[18px] text-[#6c6d71]'>
                By submitting your email address, you agree to receive our monthly newsletter. You can unsubscribe at any time.
            </p>
        </div>
    )
}

export default NewsletterForm
